// Calculate the layout properties required for each component.
// All positions/sizes from the API are in actual pixels. To render
// the prototype everything is scaled down to fit in the current
// rendered screen size.
// Note: we calculate everything in *pixels*. We tried percentage
// layouts but in CSS they are weird and unintuitive. Pixels are
// explicit, reliable, and easier to debug.
import { isNumber, mapValues } from 'lodash-es';
import { OverlayPosition } from '@marvelapp/core';
import * as screenLocation from '../constants/screenLocation';
import { getZoomScale, isInHandoffMode } from './handoff';
import { isCentredWebPrototype } from './project';
import { getCurrentScreen, getPreviousScreen, getRenderedScreenDimensions, getScreen, } from './screen';
import { getStatusBarTrimHeight } from './statusBar';
export function getScreenStyles(state, isCurrent, overlayScreen, overlayWidth, overlayHeight) {
    var renderedDimensions;
    var screen;
    if (overlayScreen) {
        renderedDimensions = { width: overlayWidth, height: overlayHeight };
        screen = overlayScreen;
    }
    else {
        screen = getScreen(state, isCurrent);
        renderedDimensions = getRenderedScreenDimensions(state);
    }
    var img = screen.img;
    var header = screen.header, footer = screen.footer;
    if (isInHandoffMode(state)) {
        header = undefined;
        footer = undefined;
    }
    var scale = getScale(state, getScreen(state, isCurrent).img);
    var fullHeaderHeight = header ? getHeaderHeight(header) : 0;
    var statusBarTrimHeight = getStatusBarTrimHeight(state);
    var fullFooterHeight = footer ? getFooterHeight(footer, img) : 0;
    var headerHeight = scale(fullHeaderHeight);
    var trimmedHeaderHeight = headerHeight;
    if (statusBarTrimHeight && header) {
        trimmedHeaderHeight = headerHeight - statusBarTrimHeight;
    }
    var footerHeight = scale(fullFooterHeight);
    var scrollableAreaHeight = renderedDimensions.height;
    var scrollableAreaWidth = renderedDimensions.width;
    var scrollableAreaPadding = {
        paddingTop: trimmedHeaderHeight,
        paddingBottom: footerHeight,
    };
    var scrollableHeight = scrollableAreaHeight - trimmedHeaderHeight - footerHeight;
    var imageHeight = scale(img.height, true - headerHeight - footerHeight);
    var isScrollableAreaScrollable = imageHeight > scrollableHeight;
    var imageWidth = getImageWidth(state, renderedDimensions, img);
    var marginLeft = 0;
    if (isInHandoffMode(state)) {
        if (renderedDimensions.width > imageWidth) {
            marginLeft = (renderedDimensions.width - imageWidth) / 2;
        }
    }
    else if (renderedDimensions.width < imageWidth) {
        if (isCentredWebPrototype(state)) {
            marginLeft = (renderedDimensions.width - imageWidth) / 2;
        }
    }
    else {
        marginLeft = (renderedDimensions.width - imageWidth) / 2;
    }
    var imageMarginTop;
    if (header) {
        imageMarginTop = -headerHeight;
    }
    else {
        imageMarginTop = -statusBarTrimHeight;
    }
    var imageMargin = {
        marginTop: imageMarginTop,
        marginBottom: -footerHeight,
        marginLeft: marginLeft,
    };
    var _a = state.screen, scrollTo = _a.scrollTo, scrollPos = _a.scrollPos;
    var scrollTop = null;
    var scrollLeft = null;
    if (isCurrent) {
        scrollTop = scrollPos.current;
        if (scrollTo !== null) {
            if (isNumber(scrollTo.top)) {
                (scrollLeft = scrollTo.left, scrollTop = scrollTo.top);
            }
            else {
                scrollTop = scrollTo;
            }
            // TODO fix for when status bar trimmed
            scrollTop = scale(scrollTop) - headerHeight;
            if (scrollLeft) {
                scrollLeft = scale(scrollLeft);
            }
            if (state.screen.scrollingToAnnotation !== null) {
                scrollTop -= 17; // TODO ANNOTATION_SIZE / 2
            }
            if (scrollLeft) {
                scrollTop = { left: scrollLeft, top: scrollTop };
            }
        }
        else {
            scrollTop = scrollPos.current;
        }
    }
    else {
        scrollTop = scrollPos.previous;
    }
    return {
        imgURL: screen.img.url,
        headerHeight: trimmedHeaderHeight,
        headerBackgroundOffset: -statusBarTrimHeight,
        footerHeight: footerHeight,
        scrollableAreaHeight: scrollableAreaHeight,
        scrollableAreaWidth: scrollableAreaWidth,
        scrollableAreaPadding: scrollableAreaPadding,
        isScrollableAreaScrollable: isScrollableAreaScrollable,
        scrollTop: scrollTop,
        imageMargin: imageMargin,
        imageWidth: imageWidth,
        imageHeight: imageHeight,
    };
}
export function getHotspotStyle(_a) {
    var state = _a.state, hotspot = _a.hotspot, isCurrentScreen = _a.isCurrentScreen, location = _a.location, screenID = _a.screenID;
    var _b = getScreen(state, screenID), header = _b.header, footer = _b.footer;
    switch (location) {
        case screenLocation.HEADER:
            return getHeaderHotspotStyle(state, hotspot);
        case screenLocation.FOOTER:
            return getFooterHotspotStyle(state, footer, hotspot);
        case screenLocation.BODY:
            return getBodyHotspotStyle(state, header, hotspot, isCurrentScreen);
        case screenLocation.FIXED: {
            return getFixedHotspotStyle(state, header, hotspot, isCurrentScreen, screenID);
        }
        case screenLocation.OVERLAY:
            return getOverlayHotspotStyle(state, hotspot);
        default:
            throw new Error("'".concat(location, "' is not a valid hotspot location"));
    }
}
function getFixedHotspotStyle(state, header, hotspot, isCurrentScreen, screenID) {
    var offset = header ? header.y : 0;
    var top = hotspot.y - offset;
    var layout = getCommonHotspotStyle(state, top, hotspot, isCurrentScreen);
    var renderedHeight = getRenderedScreenDimensions(state, screenID).height;
    var hotspotBottom = layout.top + layout.height;
    var hotspotFullyBelowViewport = hotspotBottom > renderedHeight && layout.top > renderedHeight;
    if (hotspotFullyBelowViewport) {
        var screenGetter = isCurrentScreen ? getCurrentScreen : getPreviousScreen;
        var screen = screenGetter(state);
        var currentScreenImage = screen.img;
        var distance = scaled(state, currentScreenImage, {
            distance: currentScreenImage.height - hotspot.y2,
        }).distance;
        layout.top = renderedHeight - layout.height - distance;
    }
    return layout;
}
function getHeaderHotspotStyle(state, hotspot) {
    var top = hotspot.y;
    var layout = getCommonHotspotStyle(state, top, hotspot);
    var statusBarTrimHeight = getStatusBarTrimHeight(state);
    layout.top -= statusBarTrimHeight;
    return layout;
}
function getFooterHotspotStyle(state, footer, hotspot) {
    var top = hotspot.y - footer.y;
    return getCommonHotspotStyle(state, top, hotspot);
}
function getBodyHotspotStyle(state, header, hotspot, isCurrentScreen) {
    var offset = header ? header.y : 0;
    var top = hotspot.y - offset;
    var layout = getCommonHotspotStyle(state, top, hotspot, isCurrentScreen);
    if (!header) {
        var statusBarTrimHeight = getStatusBarTrimHeight(state);
        layout.top -= statusBarTrimHeight;
    }
    return layout;
}
function getOverlayHotspotStyle(state, hotspot) {
    var top = hotspot.y;
    return getCommonHotspotStyle(state, top, hotspot);
}
function getCommonHotspotStyle(state, top, hotspot, isCurrentScreen) {
    if (isCurrentScreen === void 0) { isCurrentScreen = true; }
    var screenGetter = isCurrentScreen ? getCurrentScreen : getPreviousScreen;
    var screen = screenGetter(state);
    var currentScreenImage = screen.img;
    var left = hotspot.x;
    var _a = getHotspotSize(hotspot), width = _a.width, height = _a.height;
    return scaled(state, currentScreenImage, {
        top: top,
        left: left,
        width: width,
        height: height,
    });
}
export function getOverlayStyle(state, renderedScreenDimensions, hotspot, overlay, isCurrentScreen, positionParent, overridePosition) {
    var screenGetter = isCurrentScreen ? getCurrentScreen : getPreviousScreen;
    var screen = screenGetter(state);
    var top = overridePosition ? overridePosition[1] : hotspot.overlayY;
    var left = overridePosition ? overridePosition[0] : hotspot.overlayX;
    if (positionParent) {
        if (isManuallyPositioned(hotspot) || overridePosition) {
            var parentHotspot = state.screen.hotspotMap[positionParent];
            if (isManuallyPositioned(parentHotspot)) {
                top += parentHotspot.overlayY;
                left += parentHotspot.overlayX;
            }
            else {
                var fixedPosition = calcFixedPosition(parentHotspot, screen, overlay);
                top += fixedPosition.top;
                left += fixedPosition.left;
            }
        }
    }
    return scaled(state, screen.img, {
        top: top,
        left: left,
        width: overlay.img.width,
        height: overlay.img.height,
    });
}
function isManuallyPositioned(hotspot) {
    return hotspot.overlayPosition === OverlayPosition.Custom;
}
function calcFixedPosition(hotspot, screen, overlay) {
    switch (hotspot.overlayPosition) {
        case OverlayPosition.Centered:
            return {
                top: (screen.img.height - overlay.img.height) / 2,
                left: (screen.img.width - overlay.img.width) / 2,
            };
        case OverlayPosition.TopLeft:
            return {
                left: 0,
                top: 0,
            };
        case OverlayPosition.TopCenter:
            return {
                left: (screen.img.width - overlay.img.width) / 2,
                top: 0,
            };
        case OverlayPosition.TopRight:
            return {
                left: screen.img.width - overlay.img.width,
                top: 0,
            };
        case OverlayPosition.BottomLeft:
            return {
                top: screen.img.height - overlay.img.height,
                left: 0,
            };
        case OverlayPosition.BottomCenter:
            return {
                top: screen.img.height - overlay.img.height,
                left: (screen.img.width - overlay.img.width) / 2,
            };
        case OverlayPosition.BottomRight:
            return {
                top: screen.img.height - overlay.img.height,
                left: screen.img.width - overlay.img.width,
            };
        default:
            throw new Error("'".concat(hotspot.overlayPosition, "' is not a valid overlay position"));
    }
}
export function getCurrentScale(state) {
    var img = getCurrentScreen(state).img;
    return getScale(state, img);
}
export function getScale(state, image) {
    var renderedScreenDimensions = getRenderedScreenDimensions(state);
    var isMobile = state.deviceDetection.isMobile;
    var renderedImageWidth = getImageWidth(state, renderedScreenDimensions, image);
    var scale = renderedImageWidth / image.width;
    function scaleValue(value, raw) {
        if (raw === void 0) { raw = false; }
        var rawScaledValue = scale * value;
        if (raw) {
            return rawScaledValue;
        }
        if (!isMobile) {
            return Math.round(rawScaledValue);
        }
        return rawScaledValue;
    }
    return scaleValue;
}
function getImageWidth(state, renderedScreenSize, image) {
    var _a = state.project.config, isWeb = _a.isWeb, isResponsive = _a.isResponsive, scaleRetinaImages = _a.scaleRetinaImages;
    var actualImageWidth = image.width;
    var maxImageWidth;
    if (scaleRetinaImages && !isInHandoffMode(state)) {
        maxImageWidth = actualImageWidth / 2;
    }
    else {
        maxImageWidth = actualImageWidth;
    }
    var renderedImageWidth;
    if (isInHandoffMode(state)) {
        renderedImageWidth = maxImageWidth * getZoomScale(state);
    }
    else if (renderedScreenSize.width <= maxImageWidth) {
        if (isWeb && !isResponsive) {
            renderedImageWidth = maxImageWidth;
        }
        else {
            renderedImageWidth = renderedScreenSize.width;
        }
    }
    else {
        renderedImageWidth = maxImageWidth;
    }
    return renderedImageWidth;
}
function scaled(state, image, values) {
    var scale = getScale(state, image);
    return mapValues(values, function (value) { return scale(value); });
}
function getHeaderHeight(header) {
    return header.y;
}
function getFooterHeight(footer, image) {
    return image.height - footer.y;
}
function getHotspotSize(hotspot) {
    return {
        width: hotspot.x2 - hotspot.x,
        height: hotspot.y2 - hotspot.y,
    };
}
